import { useMemo, useState } from 'react';
import type { FunctionComponent, SVGProps } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CheckmarkIcon,
  ShieldCheckIcon,
  PermGeneralIcon,
  PermDashboardIcon,
  PermUsersIcon,
  PermOrgIcon,
  PermTaskIcon,
  PermCrIcon,
  PermInvitationsIcon,
  PermCalendarIcon,
  PermAdminIcon,
} from '../../assets/icons';
import Breadcrumbs from '../../components/Breadcrumbs';

type IconType = FunctionComponent<SVGProps<SVGSVGElement>>;
type Tone = 'danger' | 'accent';

interface Perm {
  label: string;
  tone?: Tone;
}
interface PermGroup {
  id: string;
  name: string;
  Icon: IconType;
  perms: Perm[];
  highlighted?: boolean;
}

const d = (label: string): Perm => ({ label, tone: 'danger' });
const a = (label: string): Perm => ({ label, tone: 'accent' });

const GROUPS: PermGroup[] = [
  {
    id: 'dashboard',
    name: 'Dashboard',
    Icon: PermDashboardIcon,
    perms: ['View Dashboard', 'View Team Dashboard', 'View Location Dashboard', 'View Executive Dashboard'].map((l) => ({ label: l })),
  },
  {
    id: 'users',
    name: 'User Management',
    Icon: PermUsersIcon,
    perms: [
      { label: 'View Users' },
      { label: 'Create User' },
      { label: 'Edit User' },
      d('Delete User'),
      { label: 'Activate/Deactivate User' },
      { label: 'Assign Org Position' },
      { label: 'Assign Functional Roles' },
    ],
  },
  {
    id: 'org',
    name: 'Organization',
    Icon: PermOrgIcon,
    perms: [
      { label: 'View Org Hierarchy' },
      { label: 'Create Org Unit' },
      { label: 'Edit Org Unit' },
      d('Delete Org Unit'),
      { label: 'Configure Hierarchy' },
    ],
  },
  {
    id: 'tasks',
    name: 'Task Management',
    Icon: PermTaskIcon,
    perms: [
      { label: 'View Tasks' },
      { label: 'Create Task' },
      { label: 'Edit Task' },
      { label: 'Update Task Status' },
      { label: 'Assign Task' },
      { label: 'Reassign Task' },
      d('Delete Task'),
      { label: 'Comment on Task' },
      { label: 'Attach Files' },
      { label: 'Set Priority' },
      { label: 'Set Due Date' },
      { label: 'Close Task' },
      { label: 'Reopen Task' },
    ],
  },
  {
    id: 'cr',
    name: 'Citizen Request (CR)',
    Icon: PermCrIcon,
    perms: [
      { label: 'View All CRs' },
      { label: 'Create CR' },
      { label: 'Edit CR' },
      { label: 'Assign CR' },
      { label: 'Escalate CR' },
      a('Change Priority'),
      { label: 'Add Note' },
      { label: 'Attach Evidence' },
      { label: 'Merge CR' },
      { label: 'Split CR' },
      { label: 'Resolve CR' },
      { label: 'Close CR' },
      { label: 'Reopen CR' },
    ],
  },
  {
    id: 'invitations',
    name: 'Invitations',
    Icon: PermInvitationsIcon,
    perms: [
      { label: 'Schedule Invitation' },
      { label: 'Edit Invitation' },
      { label: 'Cancel Invitation' },
      { label: 'Update Attendance' },
      { label: 'Delegate Invitation' },
      { label: 'Resend Invitation' },
      { label: 'Bulk Invite' },
      { label: 'Export Invitees' },
      { label: 'Approve Invitation' },
    ],
  },
  {
    id: 'calendar',
    name: 'Calendar',
    Icon: PermCalendarIcon,
    perms: [
      { label: 'View Calendar' },
      { label: 'Block Calendar' },
      a('Approve Schedule'),
      { label: 'Manage Events' },
      { label: 'Share Calendar' },
      { label: 'Sync External' },
    ],
  },
  {
    id: 'admin',
    name: 'Admin Controls',
    Icon: PermAdminIcon,
    highlighted: true,
    perms: [
      { label: 'Assign Permissions' },
      { label: 'Edit Role' },
      d('Delete Role'),
      { label: 'Manage Settings' },
      { label: 'View Audit Logs' },
      { label: 'Impersonate User' },
    ],
  },
];

const ALL_PERM_IDS = GROUPS.flatMap((g) => g.perms.map((p) => `${g.id}:${p.label}`));

const FIELD =
  'w-full rounded-md border border-line bg-[#f8f9ff] px-4 py-2.5 text-sm text-ink placeholder:text-[#9ca3af] transition-colors focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/15';

const TONE_CLASS: Record<Tone, string> = {
  danger: 'text-[#ba1a1a]',
  accent: 'font-semibold text-[#004ac6]',
};

function Checkbox({ checked }: { checked: boolean }) {
  return (
    <span
      className={[
        'flex size-5 shrink-0 items-center justify-center rounded-[2.5px] transition-colors',
        checked ? 'bg-brand text-white' : 'border border-[#767676] bg-white',
      ].join(' ')}
    >
      {checked && <CheckmarkIcon className="size-4" />}
    </span>
  );
}

export default function CreateRolePage() {
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const toggle = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const selectAll = () => setSelected(new Set(ALL_PERM_IDS));
  const deselectAll = () => setSelected(new Set());

  const create = () => {
    // TODO: wire to a createRole mutation once the API exists.
    // eslint-disable-next-line no-console
    console.log('Create role', { name, description, permissions: Array.from(selected) });
    navigate('/admin/roles');
  };

  const permCounts = useMemo(
    () => Object.fromEntries(GROUPS.map((g) => [g.id, g.perms.length])),
    [],
  );

  return (
    <div className="flex flex-col gap-6 pb-24">
      {/* Breadcrumb + heading */}
      <div className="flex flex-col gap-1">
        <Breadcrumbs
          items={[{ label: 'Roles and Permissions', to: '/admin/roles' }, { label: 'Create New Role' }]}
        />
        <h1 className="text-2xl font-semibold leading-9 tracking-[-0.75px] text-ink">Create New Role</h1>
        <p className="max-w-[672px] text-sm text-ink-secondary">
          Define a new functional role and assign specific permissions across system modules to
          maintain strict access control.
        </p>
      </div>

      {/* General information card */}
      <section className="overflow-hidden rounded-lg bg-white shadow-card">
        <div className="flex items-center gap-4 border-b border-line/30 px-6 py-6">
          <span className="flex size-10 items-center justify-center rounded bg-brand/10 text-brand">
            <PermGeneralIcon className="size-5" />
          </span>
          <div className="flex flex-col">
            <h2 className="text-base text-ink-900">General Information</h2>
            <span className="text-[11px] font-medium uppercase tracking-[0.55px] text-ink-secondary">
              Identity &amp; Scope
            </span>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-6 p-6 md:grid-cols-12">
          <div className="flex flex-col gap-1.5 md:col-span-5">
            <label htmlFor="role-name" className="text-base text-ink-900">
              Role Name
            </label>
            <input
              id="role-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Regional Manager"
              className={FIELD}
            />
            <span className="text-xs text-ink-secondary">
              A unique identifier for this functional group.
            </span>
          </div>
          <div className="flex flex-col gap-1.5 md:col-span-7">
            <label htmlFor="role-desc" className="text-base text-ink-900">
              Role Description
            </label>
            <textarea
              id="role-desc"
              rows={3}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Describe the scope and responsibilities of this role..."
              className={[FIELD, 'resize-none'].join(' ')}
            />
          </div>
        </div>
      </section>

      {/* Permission matrix */}
      <div className="flex flex-wrap items-center gap-4 px-1">
        <span className="text-[13px] font-bold uppercase tracking-[1.3px] text-ink-secondary">
          Permission Matrix
        </span>
        <span className="hidden h-px w-32 bg-line sm:block" />
        <div className="ml-auto flex items-center gap-4 text-[13px] font-semibold tracking-[0.65px]">
          <button type="button" onClick={selectAll} className="text-brand-dark hover:underline">
            Select All
          </button>
          <button type="button" onClick={deselectAll} className="text-ink-secondary hover:underline">
            Deselect All
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3">
        {GROUPS.map((group) => (
          <section
            key={group.id}
            className={[
              'flex flex-col gap-4 rounded-lg p-6',
              group.highlighted
                ? 'bg-[rgba(0,74,198,0.05)] shadow-[0px_0px_0px_1px_rgba(0,74,198,0.2)]'
                : 'bg-brand-shade',
            ].join(' ')}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <group.Icon
                  className={['size-5', group.highlighted ? 'text-brand-dark' : 'text-ink-secondary'].join(' ')}
                />
                <h3 className="text-base text-ink-900">{group.name}</h3>
              </div>
              <span
                className={[
                  'rounded px-2 py-0.5 text-[11px] font-medium',
                  group.highlighted ? 'bg-brand/10 text-brand-dark' : 'bg-[#d3e4fe] text-ink-secondary',
                ].join(' ')}
              >
                {permCounts[group.id]} Perms
              </span>
            </div>

            <div className="flex max-h-[192px] flex-col gap-1 overflow-auto pr-1">
              {group.perms.map((perm) => {
                const id = `${group.id}:${perm.label}`;
                const checked = selected.has(id);
                return (
                  <button
                    key={id}
                    type="button"
                    onClick={() => toggle(id)}
                    className="flex items-center gap-4 rounded p-2 text-left transition-colors hover:bg-white/60"
                  >
                    <Checkbox checked={checked} />
                    <span className={['text-sm', perm.tone ? TONE_CLASS[perm.tone] : 'text-ink-900'].join(' ')}>
                      {perm.label}
                    </span>
                  </button>
                );
              })}
            </div>
          </section>
        ))}
      </div>

      {/* Footer actions */}
      <div className="flex justify-end pt-2">
        <div className="flex items-center gap-4 rounded-xl border border-[#e9e9e9] bg-white/80 p-2 shadow-card backdrop-blur">
          <button
            type="button"
            onClick={() => navigate('/admin/roles')}
            className="rounded-xl px-8 py-2 text-base text-ink-900 transition-colors hover:bg-brand-shade"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={create}
            className="flex items-center gap-2 rounded-xl bg-brand px-8 py-2 text-base text-white shadow-[0px_10px_15px_-3px_rgba(0,74,198,0.3),0px_4px_6px_-4px_rgba(0,74,198,0.3)] transition-colors hover:bg-brand-dark"
          >
            <ShieldCheckIcon className="h-[15px] w-3" />
            Create Role
          </button>
        </div>
      </div>
    </div>
  );
}
